import React, { useState, useRef, useEffect } from 'react';
import api from '../api/axios';

const SectionDesignEditor = ({ page, settings, onUpdate }) => {
    const [elements, setElements] = useState(settings.elements || []);
    const [selectedElementId, setSelectedElementId] = useState(null);
    const canvasRef = useRef(null);
    const [isDragging, setIsDragging] = useState(false);
    const [dragOffset, setDragOffset] = useState({ x: 0, y: 0 });
    const [clientUrl, setClientUrl] = useState(process.env.REACT_APP_CLIENT_URL || 'http://localhost:3000');
    const [availableGroups, setAvailableGroups] = useState([]);

    // Sync state with parent
    useEffect(() => {
        onUpdate('elements', elements);
    }, [elements]);

    useEffect(() => {
        if (page === 'agenda') {
            const fetchGroups = async () => {
                try {
                    const res = await api.get('/agenda');
                    const groups = Array.from(new Set(res.data.map(e => e.group_name).filter(Boolean)));
                    setAvailableGroups(groups);
                } catch (err) {
                    console.error("Error fetching agenda groups", err);
                }
            };
            fetchGroups();
        }
    }, [page]);

    const handleAddElement = () => {
        const newElement = {
            id: Date.now().toString(),
            name: `Elemento ${elements.length + 1}`,
            type: 'image',
            url: '',
            x: 50,
            y: 50,
            scale: 1,
            animation: 'none',
            effect: 'none',
            zIndex: elements.length + 1
        };
        setElements([...elements, newElement]);
        setSelectedElementId(newElement.id);
    };

    const handleRemoveElement = (id) => {
        setElements(elements.filter(el => el.id !== id));
        if (selectedElementId === id) setSelectedElementId(null);
    };

    const updateElement = (id, key, value) => {
        setElements(elements.map(el => el.id === id ? { ...el, [key]: value } : el));
    };

    const handleMouseDown = (e, id) => {
        e.stopPropagation();
        setSelectedElementId(id);
        setIsDragging(true);

        const el = elements.find(el => el.id === id);
        if (!el || !canvasRef.current) return;

        const rect = canvasRef.current.getBoundingClientRect();
        const mouseX = ((e.clientX - rect.left) / rect.width) * 100;
        const mouseY = ((e.clientY - rect.top) / rect.height) * 100;

        setDragOffset({
            x: mouseX - el.x,
            y: mouseY - el.y
        });
    };

    const handleMouseMove = (e) => {
        if (!isDragging || !selectedElementId || !canvasRef.current) return;

        const rect = canvasRef.current.getBoundingClientRect();
        let newX = ((e.clientX - rect.left) / rect.width) * 100 - dragOffset.x;
        let newY = ((e.clientY - rect.top) / rect.height) * 100 - dragOffset.y;

        // Constraint to bounds (0 to 100)
        newX = Math.max(0, Math.min(100, newX));
        newY = Math.max(0, Math.min(100, newY));

        updateElement(selectedElementId, 'x', newX);
        updateElement(selectedElementId, 'y', newY);
    };

    const handleMouseUp = () => {
        setIsDragging(false);
    };

    const selectedElement = elements.find(el => el.id === selectedElementId);

    const getEffectStyle = (effect) => {
        switch(effect) {
            case 'neon': return 'drop-shadow(0 0 10px rgba(157, 78, 221, 0.8)) drop-shadow(0 0 20px rgba(157, 78, 221, 0.4))';
            case 'shadow': return 'drop-shadow(0 10px 15px rgba(0, 0, 0, 0.3))';
            case 'glow': return 'drop-shadow(0 0 15px rgba(255, 255, 255, 0.8))';
            default: return 'none';
        }
    };

    return (
        <div className="bg-slate-50 rounded-[40px] border border-slate-100 overflow-hidden group transition-all hover:shadow-2xl hover:bg-white mb-8">
            {/* Page Header */}
            <div className="px-10 py-6 bg-white/50 border-b border-slate-100 flex justify-between items-center">
                <div className="flex items-center gap-4">
                    <span className="w-3 h-3 rounded-full" style={{ backgroundColor: settings.enabled ? '#10b981' : '#94a3b8' }}></span>
                    <h3 className="text-xl font-black text-slate-900 uppercase tracking-tighter italic">{page}</h3>
                </div>
                <div className="flex gap-2">
                    <button 
                        onClick={() => onUpdate('enabled', !settings.enabled)}
                        className={`px-6 py-2 rounded-2xl text-[9px] font-black uppercase tracking-widest transition-all ${settings.enabled ? 'bg-emerald-500 text-white shadow-lg shadow-emerald-500/20' : 'bg-slate-200 text-slate-500'}`}
                    >
                        {settings.enabled ? 'Sección Activa' : 'Sección Oculta'}
                    </button>
                </div>
            </div>

            <div className="p-10 grid grid-cols-1 lg:grid-cols-12 gap-10">
                {/* General Settings */}
                <div className="lg:col-span-3 space-y-6">
                    <label className="block text-[10px] font-black text-slate-400 uppercase tracking-widest mb-2">Fondo de Sección</label>
                    <input 
                        className="w-full bg-white border border-slate-100 p-4 rounded-2xl text-[10px] font-medium shadow-sm focus:ring-4 focus:ring-slate-100 transition-all"
                        placeholder="URL del fondo"
                        value={settings.bgImage || ''}
                        onChange={e => onUpdate('bgImage', e.target.value)}
                    />
                    {settings.bgImage && (
                        <div className="w-full h-24 rounded-2xl bg-slate-200 overflow-hidden border-2 border-white shadow-md">
                            <img src={settings.bgImage} className="w-full h-full object-cover" alt="Background Preview" />
                        </div>
                    )}
                    
                    <div className="pt-6 border-t border-slate-100">
                        <button 
                            onClick={handleAddElement}
                            className="w-full bg-indigo-50 text-indigo-600 border border-indigo-100 py-3 rounded-2xl text-[10px] font-black uppercase tracking-widest hover:bg-indigo-100 transition-all"
                        >
                            + Agregar Elemento Visual
                        </button>
                    </div>

                    {page === 'gallery' && (
                        <div className="space-y-4 pt-4 border-t border-slate-100">
                            <p className="text-[8px] font-black text-slate-300 uppercase tracking-widest mb-2">Tipo de Galería Principal</p>
                            <div className="grid grid-cols-3 gap-2">
                                {['upload', 'embed', 'redirect'].map(type => (
                                    <button 
                                        key={type}
                                        onClick={() => onUpdate('type', type)}
                                        className={`py-3 rounded-xl text-[9px] font-black uppercase tracking-widest border ${settings.type === type ? 'bg-slate-900 text-white border-slate-900 shadow-md' : 'bg-white text-slate-400 border-slate-100'}`}
                                    >
                                        {type}
                                    </button>
                                ))}
                            </div>
                            {(settings.type === 'embed' || settings.type === 'redirect') && (
                                <input 
                                    className="w-full bg-white border border-slate-100 p-4 rounded-2xl text-[10px] font-mono shadow-sm" 
                                    placeholder="URL de Destino" 
                                    value={settings.url || ''} 
                                    onChange={e => onUpdate('url', e.target.value)} 
                                />
                            )}
                        </div>
                    )}

                    {page === 'agenda' && (
                        <div className="pt-4 border-t border-slate-100">
                            <button 
                                onClick={() => onUpdate('agendaImageOnly', !settings.agendaImageOnly)}
                                className={`flex items-center justify-between w-full px-5 py-3 rounded-2xl text-[9px] font-black uppercase tracking-widest transition-all mb-4 ${settings.agendaImageOnly ? 'bg-orange-50 text-orange-600 border border-orange-100' : 'bg-white border border-slate-100 text-slate-300'}`}
                            >
                                <span>🖼️ Solo Imagen</span>
                                <span>{settings.agendaImageOnly ? 'SÍ' : 'NO'}</span>
                            </button>

                            <div className="space-y-4 mb-4">
                                <div>
                                    <label className="block text-[8px] font-black text-slate-400 uppercase tracking-widest mb-1 ml-1">Color/Gradiente Pestaña Activa (Por Defecto)</label>
                                    <input 
                                        className="w-full bg-white border border-slate-100 p-3 rounded-xl text-xs shadow-sm font-mono"
                                        placeholder="ej: bg-[linear-gradient(90deg,#e17c33,#d1583e,#c0344b)]"
                                        value={settings.tabActiveGradient || 'bg-[linear-gradient(90deg,#e17c33,#d1583e,#c0344b)]'}
                                        onChange={e => onUpdate('tabActiveGradient', e.target.value)}
                                    />
                                </div>
                                <div>
                                    <label className="block text-[8px] font-black text-slate-400 uppercase tracking-widest mb-1 ml-1">Color Texto Pestaña Activa (Por Defecto)</label>
                                    <div className="flex gap-2">
                                        <input type="color" className="w-10 h-10 rounded-lg cursor-pointer border-none" value={settings.tabTextColor || '#ffffff'} onChange={e => onUpdate('tabTextColor', e.target.value)} />
                                        <input className="flex-grow bg-white border border-slate-100 p-3 rounded-xl text-xs shadow-sm font-mono" value={settings.tabTextColor || '#ffffff'} onChange={e => onUpdate('tabTextColor', e.target.value)} />
                                    </div>
                                </div>
                            </div>

                            <div className="space-y-4 mb-4 p-5 bg-purple-50 rounded-3xl border border-purple-100 animate-fadeIn">
                                <p className="text-[8px] font-black text-purple-500 uppercase tracking-widest mb-2 ml-1">Gradientes Personalizados por Grupo</p>
                                {(settings.groupColors || []).map((gc, idx) => (
                                    <div key={idx} className="bg-white p-4 rounded-2xl border border-purple-200 relative group mb-2">
                                        <button 
                                            onClick={() => {
                                                const newGc = [...(settings.groupColors || [])];
                                                newGc.splice(idx, 1);
                                                onUpdate('groupColors', newGc);
                                            }}
                                            className="absolute top-2 right-2 text-red-400 hover:text-red-600 opacity-0 group-hover:opacity-100 text-xs"
                                        >✕</button>
                                        
                                        {availableGroups.length > 0 ? (
                                            <select
                                                className="w-full bg-slate-50 border border-slate-100 p-2 rounded-lg text-xs mb-2 font-bold cursor-pointer"
                                                value={gc.groupName || ''}
                                                onChange={e => {
                                                    const newGc = [...(settings.groupColors || [])];
                                                    newGc[idx].groupName = e.target.value;
                                                    onUpdate('groupColors', newGc);
                                                }}
                                            >
                                                <option value="" disabled>Seleccione un grupo de la BD...</option>
                                                {availableGroups.map(group => (
                                                    <option key={group} value={group}>{group}</option>
                                                ))}
                                            </select>
                                        ) : (
                                            <input 
                                                className="w-full bg-slate-50 border border-slate-100 p-2 rounded-lg text-xs mb-2 font-bold" 
                                                placeholder="Nombre del Grupo (ej: SHUA - HPN)" 
                                                value={gc.groupName} 
                                                onChange={e => {
                                                    const newGc = [...(settings.groupColors || [])];
                                                    newGc[idx].groupName = e.target.value;
                                                    onUpdate('groupColors', newGc);
                                                }} 
                                            />
                                        )}
                                        
                                        <input 
                                            className="w-full bg-slate-50 border border-slate-100 p-2 rounded-lg text-[10px] font-mono mb-2"  
                                            placeholder="Gradiente: bg-[linear-gradient(...)]" 
                                            value={gc.gradient} 
                                            onChange={e => {
                                                const newGc = [...(settings.groupColors || [])];
                                                newGc[idx].gradient = e.target.value;
                                                onUpdate('groupColors', newGc);
                                            }} 
                                        />
                                        <input 
                                            className="w-full bg-slate-50 border border-slate-100 p-2 rounded-lg text-[10px] font-mono mb-2" 
                                            placeholder="Color de Texto: #ffffff" 
                                            value={gc.textColor} 
                                            onChange={e => {
                                                const newGc = [...(settings.groupColors || [])];
                                                newGc[idx].textColor = e.target.value;
                                                onUpdate('groupColors', newGc);
                                            }} 
                                        />
                                        <input 
                                            className="w-full bg-slate-50 border border-slate-100 p-2 rounded-lg text-[10px] font-mono" 
                                            placeholder="URL Descarga (PDF/Imagen)" 
                                            value={gc.downloadUrl || ''} 
                                            onChange={e => {
                                                const newGc = [...(settings.groupColors || [])];
                                                newGc[idx].downloadUrl = e.target.value;
                                                onUpdate('groupColors', newGc);
                                            }} 
                                        />
                                    </div>
                                ))}
                                <button 
                                    onClick={() => {
                                        const current = settings.groupColors || [];
                                        onUpdate('groupColors', [...current, { groupName: 'Nuevo Grupo', gradient: 'bg-[linear-gradient(90deg,#e17c33,#d1583e,#c0344b)]', textColor: '#ffffff' }]);
                                    }}
                                    className="w-full bg-white text-purple-600 py-2 rounded-xl text-[9px] font-black uppercase tracking-widest border border-purple-200 hover:bg-purple-100 transition-colors"
                                >
                                    + Agregar Gradiente a Grupo
                                </button>
                            </div>

                            {settings.agendaImageOnly && (
                                <div className="p-5 bg-orange-50 rounded-3xl border border-orange-100 animate-fadeIn space-y-4">
                                    <p className="text-[8px] font-black text-orange-400 uppercase tracking-widest mb-2 ml-1">Imágenes de Agenda por Grupo</p>
                                    
                                    {(settings.imageAgendas || []).map((ag, idx) => (
                                        <div key={idx} className="bg-white p-4 rounded-2xl border border-orange-200 relative group">
                                            <button 
                                                onClick={() => {
                                                    const newAgs = [...(settings.imageAgendas || [])];
                                                    newAgs.splice(idx, 1);
                                                    onUpdate('imageAgendas', newAgs);
                                                }}
                                                className="absolute top-2 right-2 text-red-400 hover:text-red-600 opacity-0 group-hover:opacity-100 text-xs"
                                            >✕</button>
                                            <input 
                                                className="w-full bg-slate-50 border border-slate-100 p-2 rounded-lg text-xs mb-2 font-bold" 
                                                placeholder="Nombre del Grupo (ej: Día 1)" 
                                                value={ag.group_name} 
                                                onChange={e => {
                                                    const newAgs = [...(settings.imageAgendas || [])];
                                                    newAgs[idx].group_name = e.target.value;
                                                    onUpdate('imageAgendas', newAgs);
                                                }} 
                                            />
                                            <input 
                                                className="w-full bg-slate-50 border border-slate-100 p-2 rounded-lg text-[10px] font-mono mb-2" 
                                                placeholder="URL de la Imagen" 
                                                value={ag.staticImage} 
                                                onChange={e => {
                                                    const newAgs = [...(settings.imageAgendas || [])];
                                                    newAgs[idx].staticImage = e.target.value;
                                                    onUpdate('imageAgendas', newAgs);
                                                }} 
                                            />
                                            <input 
                                                className="w-full bg-slate-50 border border-slate-100 p-2 rounded-lg text-[10px] font-mono" 
                                                placeholder="URL Descarga (PDF/Imagen)" 
                                                value={ag.downloadUrl || ''} 
                                                onChange={e => {
                                                    const newAgs = [...(settings.imageAgendas || [])];
                                                    newAgs[idx].downloadUrl = e.target.value;
                                                    onUpdate('imageAgendas', newAgs);
                                                }} 
                                            />
                                        </div>
                                    ))}

                                    <button 
                                        onClick={() => {
                                            const current = settings.imageAgendas || [];
                                            onUpdate('imageAgendas', [...current, { group_name: 'Nuevo Grupo', staticImage: '' }]);
                                        }}
                                        className="w-full bg-white text-orange-500 py-2 rounded-xl text-[9px] font-black uppercase tracking-widest border border-orange-200 hover:bg-orange-100 transition-colors"
                                    >
                                        + Agregar Grupo
                                    </button>
                                </div>
                            )}
                        </div>
                    )}

                    {page === 'home' && (
                        <div className="pt-4 border-t border-slate-100">
                            <p className="block text-[10px] font-black text-slate-400 uppercase tracking-widest mb-4">Videos Configurables</p>
                            <div className="space-y-4 max-h-[300px] overflow-y-auto pr-2">
                                {(settings.videos || []).map((video, idx) => (
                                    <div key={idx} className="bg-white p-4 rounded-2xl border border-slate-200 shadow-sm relative group">
                                        <button 
                                            onClick={() => {
                                                const newVids = [...(settings.videos || [])];
                                                newVids.splice(idx, 1);
                                                onUpdate('videos', newVids);
                                            }}
                                            className="absolute top-2 right-2 text-slate-300 hover:text-red-500 text-xs opacity-0 group-hover:opacity-100 transition-opacity"
                                        >✕</button>
                                        
                                        <div className="space-y-3">
                                            <div className="flex items-center gap-2">
                                                <input 
                                                    type="checkbox" 
                                                    checked={video.active} 
                                                    onChange={e => {
                                                        const newVids = [...(settings.videos || [])];
                                                        newVids[idx].active = e.target.checked;
                                                        onUpdate('videos', newVids);
                                                    }} 
                                                    className="w-4 h-4 accent-purple-600"
                                                />
                                                <input 
                                                    className="flex-grow bg-slate-50 border border-slate-100 p-2 rounded-lg text-xs font-bold" 
                                                    placeholder="Título del Video" 
                                                    value={video.title} 
                                                    onChange={e => {
                                                        const newVids = [...(settings.videos || [])];
                                                        newVids[idx].title = e.target.value;
                                                        onUpdate('videos', newVids);
                                                    }} 
                                                />
                                            </div>
                                            <input 
                                                className="w-full bg-slate-50 border border-slate-100 p-2 rounded-lg text-[10px] font-mono" 
                                                placeholder="URL de YouTube Embed o MP4" 
                                                value={video.videoUrl} 
                                                onChange={e => {
                                                    const newVids = [...(settings.videos || [])];
                                                    newVids[idx].videoUrl = e.target.value;
                                                    onUpdate('videos', newVids);
                                                }} 
                                            />
                                            <input 
                                                className="w-full bg-slate-50 border border-slate-100 p-2 rounded-lg text-[10px] font-mono" 
                                                placeholder="URL de Miniatura (Opcional)" 
                                                value={video.thumbnailUrl} 
                                                onChange={e => {
                                                    const newVids = [...(settings.videos || [])];
                                                    newVids[idx].thumbnailUrl = e.target.value;
                                                    onUpdate('videos', newVids);
                                                }} 
                                            />
                                        </div>
                                    </div>
                                ))}
                            </div>
                            <button 
                                onClick={() => {
                                    const current = settings.videos || [];
                                    onUpdate('videos', [...current, { title: 'Nuevo Video', videoUrl: '', thumbnailUrl: '', active: true }]);
                                }}
                                className="mt-4 w-full bg-slate-100 text-slate-500 py-3 rounded-2xl text-[10px] font-black uppercase tracking-widest hover:bg-slate-200 transition-colors"
                            >
                                + Añadir Video
                            </button>
                        </div>
                    )}

                    <div className="space-y-2 mt-4 max-h-[300px] overflow-y-auto">
                        {elements.map(el => (
                            <div 
                                key={el.id}
                                onClick={() => setSelectedElementId(el.id)}
                                className={`p-3 rounded-xl cursor-pointer border flex justify-between items-center transition-all ${selectedElementId === el.id ? 'bg-indigo-600 text-white border-indigo-600 shadow-lg' : 'bg-white border-slate-100 text-slate-600 hover:border-indigo-200'}`}
                            >
                                <span className="text-xs font-bold truncate">{el.name}</span>
                                <button 
                                    onClick={(e) => { e.stopPropagation(); handleRemoveElement(el.id); }}
                                    className={`text-xs ${selectedElementId === el.id ? 'text-white/70 hover:text-white' : 'text-slate-400 hover:text-red-500'}`}
                                >
                                    ✕
                                </button>
                            </div>
                        ))}
                    </div>
                </div>

                {/* Editor & Iframe */}
                <div className="lg:col-span-9 grid grid-cols-1 md:grid-cols-2 gap-8">
                    
                    {/* Visual Canvas Editor */}
                    <div className="space-y-4">
                        <label className="block text-[10px] font-black text-slate-400 uppercase tracking-widest mb-2">Editor Visual</label>
                        <div 
                            className="relative bg-slate-200 rounded-[30px] border-4 border-white shadow-xl overflow-hidden aspect-video select-none"
                            ref={canvasRef}
                            onMouseMove={handleMouseMove}
                            onMouseUp={handleMouseUp}
                            onMouseLeave={handleMouseUp}
                        >
                            {/* Background */}
                            {settings.bgImage && (
                                <img src={settings.bgImage} alt="Background" className="absolute inset-0 w-full h-full object-cover pointer-events-none" />
                            )}
                            
                            {/* Elements */}
                            {elements.map(el => (
                                <div
                                    key={el.id}
                                    onMouseDown={(e) => handleMouseDown(e, el.id)}
                                    className={`absolute cursor-move transition-transform ${selectedElementId === el.id ? 'ring-2 ring-indigo-500 ring-offset-2' : ''}`}
                                    style={{
                                        left: `${el.x}%`,
                                        top: `${el.y}%`,
                                        transform: `translate(-50%, -50%) scale(${el.scale || 1})`,
                                        zIndex: el.zIndex || 10,
                                        filter: getEffectStyle(el.effect)
                                    }}
                                >
                                    {el.url ? (
                                        <img src={el.url} alt={el.name} className="max-w-[150px] max-h-[150px] object-contain pointer-events-none" />
                                    ) : (
                                        <div className="w-20 h-20 bg-slate-300/80 rounded-xl flex items-center justify-center border-2 border-dashed border-slate-400 text-[10px] font-bold text-slate-600 p-2 text-center pointer-events-none backdrop-blur-sm">
                                            Sin Imagen
                                        </div>
                                    )}
                                </div>
                            ))}
                        </div>

                        {/* Selected Element Properties */}
                        {selectedElement && (
                            <div className="bg-white p-6 rounded-[20px] shadow-sm border border-slate-100 animate-fadeIn space-y-4">
                                <h4 className="text-xs font-black uppercase tracking-widest text-slate-800 border-b pb-2">Propiedades: {selectedElement.name}</h4>
                                <div className="grid grid-cols-2 gap-4">
                                    <div className="col-span-2">
                                        <label className="block text-[9px] font-black text-slate-400 uppercase tracking-widest mb-1">Nombre</label>
                                        <input 
                                            className="w-full bg-slate-50 border border-slate-100 p-3 rounded-xl text-xs"
                                            value={selectedElement.name}
                                            onChange={(e) => updateElement(selectedElement.id, 'name', e.target.value)}
                                        />
                                    </div>
                                    <div className="col-span-2">
                                        <label className="block text-[9px] font-black text-slate-400 uppercase tracking-widest mb-1">URL Imagen</label>
                                        <input 
                                            className="w-full bg-slate-50 border border-slate-100 p-3 rounded-xl text-xs font-mono"
                                            placeholder="URL PNG/SVG"
                                            value={selectedElement.url}
                                            onChange={(e) => updateElement(selectedElement.id, 'url', e.target.value)}
                                        />
                                    </div>
                                    <div>
                                        <label className="block text-[9px] font-black text-slate-400 uppercase tracking-widest mb-1">Escala ({selectedElement.scale || 1}x)</label>
                                        <input 
                                            type="range"
                                            min="0.1" max="3" step="0.1"
                                            className="w-full accent-indigo-600"
                                            value={selectedElement.scale || 1}
                                            onChange={(e) => updateElement(selectedElement.id, 'scale', parseFloat(e.target.value))}
                                        />
                                    </div>
                                    <div>
                                        <label className="block text-[9px] font-black text-slate-400 uppercase tracking-widest mb-1">Efecto</label>
                                        <select 
                                            className="w-full bg-slate-50 border border-slate-100 p-3 rounded-xl text-xs appearance-none"
                                            value={selectedElement.effect || 'none'}
                                            onChange={(e) => updateElement(selectedElement.id, 'effect', e.target.value)}
                                        >
                                            <option value="none">Sin Efecto</option>
                                            <option value="shadow">Sombreado Básico</option>
                                            <option value="neon">Neón Difusión</option>
                                            <option value="glow">Glow Blanco</option>
                                        </select>
                                    </div>
                                    <div className="col-span-2">
                                        <label className="block text-[9px] font-black text-slate-400 uppercase tracking-widest mb-1">Animación de Entrada</label>
                                        <select 
                                            className="w-full bg-slate-50 border border-slate-100 p-3 rounded-xl text-xs appearance-none"
                                            value={selectedElement.animation || 'none'}
                                            onChange={(e) => updateElement(selectedElement.id, 'animation', e.target.value)}
                                        >
                                            <option value="none">Sin Animación</option>
                                            <option value="fade-in">Fade In</option>
                                            <option value="slide-left">Slide Izquierda</option>
                                            <option value="slide-right">Slide Derecha</option>
                                            <option value="bounce">Bounce</option>
                                        </select>
                                    </div>
                                </div>
                            </div>
                        )}
                    </div>

                    {/* Iframe Preview */}
                    <div className="space-y-4">
                        <div className="flex justify-between items-center">
                            <label className="block text-[10px] font-black text-slate-400 uppercase tracking-widest mb-2">Previsualización (Iframe)</label>
                            <span className="text-[8px] bg-emerald-100 text-emerald-600 px-2 py-1 rounded-md font-bold uppercase tracking-widest animate-pulse">Live</span>
                        </div>
                        <div className="relative bg-slate-100 rounded-[30px] border-4 border-slate-800 shadow-2xl overflow-hidden aspect-[9/16] md:aspect-[3/4] mx-auto max-w-[350px]">
                            {/* Device Notch Simulation */}
                            <div className="absolute top-0 inset-x-0 h-6 bg-slate-800 rounded-b-2xl w-1/2 mx-auto z-50"></div>
                            <iframe 
                                src={`${clientUrl}/${page === 'home' ? '' : page}`} 
                                title={`Preview ${page}`}
                                className="w-full h-full bg-white border-none"
                            />
                        </div>
                        <p className="text-center text-[9px] text-slate-400 uppercase font-bold tracking-widest">Vista Móvil Aproximada</p>
                    </div>

                </div>
            </div>
        </div>
    );
};

export default SectionDesignEditor;
